// src/features/procedures/pages/CreateProcedurePage.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
// Hooks
import { useProcedures } from '../hooks/useProcedures';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import ProcedureStepsEditor from '../components/ProcedureStepsEditor';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

export default function CreateProcedurePage() {
  const { t } = useTranslation(['procedures', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // Template data from location state
  const templateProcedure = location.state?.templateProcedure;

  // State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    template_id: null,
  });
  const [steps, setSteps] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedStepId, setExpandedStepId] = useState(null);

  // API hooks
  const {
    createProcedure,
    loading: creating,
    error,
    clearError,
  } = useProcedures();

  // Load template data if available
  useEffect(() => {
    if (templateProcedure) {
      setFormData(prev => ({
        ...prev,
        name: templateProcedure.name || '',
        description: templateProcedure.description || '',
        template_id: templateProcedure.id || null,
      }));
      if (templateProcedure.steps && templateProcedure.steps.length > 0) {
        // Add step_id to each template step if not already present
        const stepsWithIds = templateProcedure.steps.map((step, index) => ({
          ...step,
          step_id: step.step_id || `temp-${Date.now()}-${index}`,
        }));
        setSteps(stepsWithIds);
      }
    }
  }, [templateProcedure]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('procedures:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('procedures:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('procedures:validation.name_max_length');
    }

    if (formData.description && formData.description.length > 2000) {
      errors.description = t('procedures:validation.description_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const createData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        is_active: formData.is_active,
        template_id: formData.template_id || null,
        steps: steps.map(step => ({
          step_id: null,
          step_order: step.step_order,
          step_type: step.step_type,
          label: step.label,
          description: step.description || null,
          is_required: step.is_required !== false,
          validation_rules: step.validation_rules || null,
          options: step.options || null,
          ai_model: step.ai_model || null,
        })),
      };

      const result = await createProcedure(createData);

      if (result.success) {
        showSuccess(t('procedures:success.procedure_created'));
        navigate(`/procedures/${result.data.data.procedure_id}`);
      } else {
        showError(result.error || t('procedures:errors.create_failed'));
      }
    } catch (error) {
      showError(t('procedures:errors.unexpected_error'));
    }
  }, [validateForm, formData, steps, createProcedure, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/procedures');
  }, [navigateBack]);

  return (
    <FormPage
      title={t('procedures:create_procedure')}
      subtitle={t('procedures:form.create_title')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
      maxWidth={1000}
    >
      {templateProcedure && (
        <Alert severity="info">
          {t('procedures:catalog.template_loaded', { name: templateProcedure.name })}
        </Alert>
      )}

      <FormSection title={t('procedures:form.create_procedure_information')}>
        <TextField
          fullWidth
          label={t('procedures:labels.name')}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name || t('procedures:form.name_helper')}
          required
          disabled={creating}
          placeholder={t('procedures:placeholders.name')}
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('procedures:labels.description')}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          error={!!validationErrors.description}
          helperText={validationErrors.description || t('procedures:form.description_helper')}
          disabled={creating}
          placeholder={t('procedures:placeholders.description')}
          inputProps={{ maxLength: 2000 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              disabled={creating}
            />
          }
          label={t('procedures:labels.active')}
        />
      </FormSection>

      <FormSection>
        <ProcedureStepsEditor
          steps={steps}
          onStepsChange={setSteps}
          expandedStepId={expandedStepId}
          onExpandedStepIdChange={setExpandedStepId}
          disabled={creating}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={creating ? t('procedures:actions.creating') : t('procedures:actions.create_procedure')}
        submitIcon={<SaveIcon />}
        loading={creating}
      />
    </FormPage>
  );
}
